import { useState } from 'react';
import { Check, Copy, ExternalLink, Eye, EyeOff, Pencil, Star, Trash2 } from 'lucide-react';
import {
  VENDOR_PAYMENT_METHOD_SPECS,
  formatCurrency,
  isPositiveAmount,
  maskAccountNumber,
  paymentMethodCopyText,
  vendorPaymentLink,
  VendorPaymentMethodType,
  type VendorPaymentMethod,
} from '@peles/shared';

/** What is being paid — the amount prefilled on a pay link and the note beside it. */
export interface PaymentTarget {
  amount?: number | null;
  note?: string | null;
}

export interface PaymentMethodCardProps {
  method: VendorPaymentMethod;
  payment?: PaymentTarget;
  onEdit?: () => void;
  onDelete?: () => void;
  /**
   * Who is paying: the office (the default) or a client in the portal. It
   * decides whose bank the instructions name, and whether to suggest adding
   * details only the office can add.
   */
  audience?: 'office' | 'client';
}

/** Where a method has no pay link, what to do instead with the amount owed. */
function howToSend(
  type: VendorPaymentMethodType,
  amount: number,
  audience: 'office' | 'client',
): string {
  const owed = formatCurrency(amount);
  const bank = audience === 'client' ? 'your' : "the office's";
  switch (type) {
    case VendorPaymentMethodType.ZELLE:
      return `Send ${owed} with Zelle from ${bank} bank app.`;
    case VendorPaymentMethodType.WIRE_ACH:
      return `Send ${owed} by wire or ACH from ${bank} bank.`;
    case VendorPaymentMethodType.CHECK:
      return `Mail a check for ${owed} to the address above.`;
    default:
      return audience === 'client'
        ? `Send ${owed} to this PayPal email from your PayPal account.`
        : `Send ${owed} to this PayPal email — add a PayPal.Me username for a pay link.`;
  }
}

const actionClass =
  'min-h-11 sm:min-h-8 inline-flex items-center gap-1.5 px-3 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50';

/**
 * One way to pay a vendor: its details (an account number masked until
 * asked for), a copy of them for a bank's transfer form, and — for Venmo and
 * PayPal — a link that opens the service ready to pay the amount owed.
 */
export function PaymentMethodCard({
  method,
  payment,
  onEdit,
  onDelete,
  audience = 'office',
}: PaymentMethodCardProps) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const spec = VENDOR_PAYMENT_METHOD_SPECS[method.type];
  const details = method.details as unknown as Record<string, string | null | undefined>;
  const amount =
    payment?.amount != null && isPositiveAmount(payment.amount) ? payment.amount : null;
  const payLink = vendorPaymentLink(method, { amount, note: payment?.note });
  const hasSensitive = spec.fields.some((f) => f.sensitive && details[f.key]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(paymentMethodCopyText(method));
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopyFailed(true);
    }
  };

  return (
    <li
      className="rounded-lg border border-gray-200 bg-white p-3 space-y-2"
      data-testid={`payment-method-${method.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-primary-100 text-primary-700">
            {spec.label}
          </span>
          {method.label && (
            <span className="text-sm font-medium text-gray-900">{method.label}</span>
          )}
          {method.isPreferred && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
              <Star size={12} className="fill-current" aria-hidden="true" />
              Preferred
            </span>
          )}
        </div>
        {(onEdit || onDelete) && (
          <div className="flex items-center gap-1 shrink-0">
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                aria-label={`Edit ${spec.label}`}
                className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center text-gray-400 hover:text-primary-600 rounded-lg"
              >
                <Pencil size={16} />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                aria-label={`Remove ${spec.label}`}
                className="min-h-11 min-w-11 sm:min-h-8 sm:min-w-8 flex items-center justify-center text-gray-400 hover:text-red-600 rounded-lg"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        )}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {spec.fields.map((field) => {
          const raw = details[field.key];
          if (!raw) return null;
          const shown =
            field.options?.find((o) => o.value === raw)?.label ??
            (field.sensitive && !revealed ? maskAccountNumber(raw) : raw);
          return (
            <div key={field.key} className="min-w-0">
              <dt className="text-xs text-gray-500">{field.label}</dt>
              <dd className="text-gray-900 break-words font-medium">{shown}</dd>
            </div>
          );
        })}
      </dl>

      {method.notes && <p className="text-xs text-gray-600 whitespace-pre-wrap">{method.notes}</p>}

      <div className="flex flex-wrap items-center gap-2 pt-1">
        {payLink && (
          <a
            href={payLink}
            target="_blank"
            rel="noopener noreferrer"
            className="min-h-11 sm:min-h-8 inline-flex items-center gap-1.5 px-3 text-sm rounded-lg bg-primary-600 text-white hover:bg-primary-700"
          >
            <ExternalLink size={14} aria-hidden="true" />
            {amount != null
              ? `Pay ${formatCurrency(amount)} with ${spec.label}`
              : `Open ${spec.label}`}
          </a>
        )}
        <button type="button" onClick={() => void copy()} className={actionClass}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy details'}
        </button>
        {hasSensitive && (
          <button type="button" onClick={() => setRevealed((r) => !r)} className={actionClass}>
            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
            {revealed ? 'Hide account number' : 'Show account number'}
          </button>
        )}
      </div>
      {!payLink && amount != null && (
        <p className="text-xs text-gray-500">{howToSend(method.type, amount, audience)}</p>
      )}
      {copyFailed && (
        <p role="alert" className="text-xs text-red-600">
          Copying is blocked here — select the details and copy them by hand.
        </p>
      )}
    </li>
  );
}
